import React, { useEffect, useState } from "react";
import axios from "axios";
import ipconfig from "../../Core/ipconfig";
import { Box, Typography, Grid, Card, CardContent, useTheme } from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import PaidIcon from "@mui/icons-material/Paid";
import SyncIcon from "@mui/icons-material/Sync";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Spinner from "../../Shared/Feature/LoadingSpinner/Spinner";

interface PartnerStatsProps {
  partnerId: string;
  onNavigate?: (tabIndex: number, filter?: string) => void;
}

export default function PartnerStats({ partnerId, onNavigate }: PartnerStatsProps) {
  const theme = useTheme();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${ipconfig}/partner/stats?partnerId=${partnerId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setStats(res.data || {});
    } catch (err) {
      console.error("Error fetching stats", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (partnerId) {
      fetchStats();
    }
  }, [partnerId]);

  if (loading || !stats) {
    return <Spinner height="150px" />;
  }

  const statItems = [
    {
      label: "Total Referred",
      value: stats.totalReferred ?? 0,
      icon: <PeopleIcon sx={{ fontSize: 24 }} />,
      color: "primary.main",
      bgColor: theme.palette.mode === "light" ? "#e0f2fe" : "#0369a130",
      targetTab: 1,
      filter: "all"
    },
    {
      label: "Paid Customers",
      value: stats.customersPaid ?? 0,
      icon: <PaidIcon sx={{ fontSize: 24 }} />,
      color: "success.main",
      bgColor: theme.palette.mode === "light" ? "#dcfce7" : "#15803d30",
      targetTab: 1,
      filter: "paid"
    },
    {
      label: "Synced Customers",
      value: stats.customersSynced ?? 0,
      icon: <SyncIcon sx={{ fontSize: 24 }} />,
      color: "info.main",
      bgColor: theme.palette.mode === "light" ? "#e0e7ff" : "#3730a330",
      targetTab: 1,
      filter: "synced"
    },
    {
      label: "Pending Payout",
      value: `₹${stats.pendingCommission ?? 0}`,
      icon: <AccessTimeIcon sx={{ fontSize: 24 }} />,
      color: "warning.main",
      bgColor: theme.palette.mode === "light" ? "#fef3c7" : "#b4530930",
      targetTab: 3,
      filter: "pending"
    },
    {
      label: "Commission Earned",
      value: `₹${stats.commissionEarned ?? 0}`,
      icon: <MonetizationOnIcon sx={{ fontSize: 24 }} />,
      color: "success.main",
      bgColor: theme.palette.mode === "light" ? "#dcfce7" : "#15803d30",
      targetTab: 3,
      filter: "earned"
    },
  ];

  return (
    <Grid container spacing={2}>
      {statItems.map((item) => (
        <Grid item xs={12} sm={6} md={2.4} key={item.label}>
          <Card
            variant="outlined"
            onClick={() => onNavigate && onNavigate(item.targetTab, item.filter)}
            sx={{
              borderRadius: "12px",
              borderColor: "divider",
              cursor: "pointer",
              transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 8px 20px rgba(37, 99, 235, 0.12)",
                borderColor: item.color
              }
            }}
          >
            <CardContent sx={{ p: "16px !important", display: "flex", flexDirection: "column", gap: "12px" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "42px",
                    height: "42px",
                    borderRadius: "10px",
                    backgroundColor: item.bgColor,
                    color: item.color,
                  }}
                >
                  {item.icon}
                </Box>
                <ArrowForwardIcon sx={{ fontSize: 16, color: "text.secondary", opacity: 0.7 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "text.primary" }}>
                  {item.value}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600, fontSize: "13px" }}>
                  {item.label}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}